import React, {FunctionComponent, useCallback, useEffect, useState} from "react";
import qs from 'query-string';

import { ScaleExercise } from '../../models/scale-exercise';
import { useQueryPitchClasses } from "../../services/graphql/use-pitch-classes";
import { useQueryModes } from "../../services/graphql/use-modes";
import { useQueryRanges } from "../../services/graphql/use-ranges";
import {ModeName} from '../../models/mode';
import {PitchClassName} from '../../models/pitch-class';
import {RangeName} from '../../models/range';
import Select from "../../components/forms/select";

interface ExerciseSelector {
  exercise: ScaleExercise;
  updateExercise: (newExercise: ScaleExercise) => any;
}

const ExerciseSelector: FunctionComponent<ExerciseSelector> = (props) => {
  const vm = useVM(props);
  
  return (
    <div className='flex my-4'>
      <div className='flex-1'>
        {
          vm.pitchClasses &&
          <Select
            className='px-2'
            label='Key'
            onChange={(e) => vm.updateField('pitch_class', e.target.value as PitchClassName)}
            value={vm.exercise.pitch_class}
          >
            {
              [...vm.pitchClasses]
                .sort((a, b) => (a.order_rel && b.order_rel) ? a.order_rel.order - b.order_rel.order : -1)
                .map(pc => <option key={pc.value} value={pc.value}>{pc.description}</option>)}
          </Select>
        }
      </div>

      <div className='flex-1'>
        {
          vm.modes &&
          <Select
            className='px-2'
            onChange={(e) => vm.updateField('mode', e.target.value as ModeName)}
            label='Mode'
            value={vm.exercise.mode}
          >
            {vm.modes.map(mode => <option key={mode.value} value={mode.value}>{mode.description}</option>)}
          </Select>
        }
      </div>

      <div className='flex-1'>
        {
          vm.ranges &&
          <Select
            className='px-2'
            onChange={(e) => vm.updateField('range', ((e.target as HTMLSelectElement).value) as RangeName)}
            label='Range'
            value={vm.exercise.range}
          >
            {vm.ranges.map(range => <option key={range.value} value={range.value}>{range.description}</option>)}
          </Select>
        }
      </div>
    </div>
  );
}

function useVM({exercise, updateExercise}: ExerciseSelector) {
  const {data: pitchClasses} = useQueryPitchClasses({where: {}});
  const {data: modes} = useQueryModes({where: {}});
  const {data: ranges} = useQueryRanges({where: {}});

  const [params] = useState(qs.parse(window.location.search));

  const updateField = useCallback(function <T extends keyof ScaleExercise>(field: T, value: ScaleExercise[T]) {
    updateExercise({
      ...exercise,
      [field]: value
    });
  }, [exercise, updateExercise])

  function handleQueryParams(pitch_class: PitchClassName, mode: ModeName, range: RangeName) {
    updateExercise({
      ...exercise,
      pitch_class,
      mode,
      range,
    });
  }

  useEffect(() => {
    if (params.mode && params.pitch_class && params.range){
      handleQueryParams(params.pitch_class as PitchClassName, params.mode as ModeName, params.range as RangeName);
    }
  }, [params]);

  return {
    exercise,
    modes,
    pitchClasses,
    ranges,
    updateField,
  }
}

export default ExerciseSelector;
